import React, { useCallback, useEffect, useRef } from 'react';

import { useInView } from '../../hooks/useInView';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import type { AthletePost, Athletes } from './slides';

import './AthletesSection.css';

type AthletesSectionProps = {
    data: Athletes;
};

// Cards fade up one after another rather than all at once.
const REVEAL_STEP_MS = 80;

// A CSS animation runs for a fixed duration, so a longer track would drift
// faster. Scaling the duration by the number of cards holds the speed steady
// however many posts get added. This is the one number to change to retune the
// marquee: lower is faster, and each card is 266px wide including its gap, so
// the speed works out at 266 / this, in pixels per second.
const MARQUEE_SECONDS_PER_CARD = 4.6;

// How far outside the viewport a clip starts loading and playing. Wide enough
// that a card has buffered by the time it drifts into view, narrow enough that
// the off-screen half of the track stays idle. Roughly one card's width.
const CLIP_PRELOAD_MARGIN = '0px 280px';

// The duplicate copy only pays for itself while the marquee animates, so it is
// skipped on a phone and under reduced motion. Skipped rather than hidden: a
// display:none <video> still counts as a media element and a phone runs out of
// those, which is why AthleteCarousel gates its desktop track too. Both queries
// match AthletesSection.css, so the JS and the CSS cannot disagree.
const PHONE_WIDTH = '(max-width: 768px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

// The same card in the marquee's other copy: a-3 and b-3 show the same post.
function twinKey(key: string): string {
    return `${key.startsWith('a-') ? 'b' : 'a'}${key.slice(1)}`;
}

type CardProps = {
    item: AthletePost;
    labels: Pick<Athletes, 'gmvLabel' | 'viewsLabel'>;
    revealDelayMs: number;
    registerVideo: (key: string, el: HTMLVideoElement | null) => void;
    videoKey: string;
    // The marquee's second copy: same pixels, but duplicate content as far as a
    // screen reader is concerned. Only rendered while the marquee animates.
    clone?: boolean;
};

function AthleteCard({ item, labels, revealDelayMs, registerVideo, videoKey, clone }: CardProps) {
    return (
        <li
            className="fb-athletes__card faves-reveal"
            style={{ '--reveal-delay': `${revealDelayMs}ms` } as React.CSSProperties}
            aria-hidden={clone || undefined}
        >
            {item.video && (
                <video
                    className="fb-athletes__media"
                    ref={(el) => registerVideo(videoKey, el)}
                    src={item.video}
                    poster={item.image || undefined}
                    muted
                    loop
                    playsInline
                    // The observer decides when a clip loads, and play() pulls the
                    // file down on its own. "metadata" would fetch all of them up
                    // front, which is the cost the observer exists to avoid. The
                    // poster is a separate request and still loads.
                    preload="none"
                    aria-hidden="true"
                    tabIndex={-1}
                />
            )}
            {/* An empty src resolves to the current page and fetches the document
                again, so a card with no media renders none. */}
            {!item.video && item.image && (
                <img
                    className="fb-athletes__media"
                    src={item.image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                />
            )}
            <div className="fb-athletes__overlay">
                {item.name && <span className="fb-athletes__capsule faves-glass">{item.name}</span>}
                {/* A blank value hides its capsule rather than printing a bare label, so
                    leaving the CMS field empty is how a figure gets withheld. */}
                {item.gmv && (
                    <span className="fb-athletes__capsule faves-glass">{`${labels.gmvLabel}: ${item.gmv}`}</span>
                )}
                {item.views && (
                    <span className="fb-athletes__capsule faves-glass">{`${labels.viewsLabel}: ${item.views}`}</span>
                )}
            </div>
        </li>
    );
}

export function AthletesSection({ data }: AthletesSectionProps) {
    const [ref, , revealed] = useInView<HTMLElement>();
    const isPhone = useMediaQuery(PHONE_WIDTH);
    const reducedMotion = useMediaQuery(REDUCED_MOTION);
    const marqueeAnimates = !isPhone && !reducedMotion;
    const videos = useRef<Map<string, HTMLVideoElement>>(new Map());
    const videoKeys = useRef<Map<HTMLVideoElement, string>>(new Map());
    const clipObserver = useRef<IntersectionObserver | null>(null);

    // Each clip is watched on its own rather than the section as a whole. The
    // track carries the list twice, so a section-wide toggle runs 2N decoders
    // for the handful of cards a viewport can actually show: at 1440px that is
    // sixteen decoders for about six visible cards.
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const leaving = entries.filter((e) => !e.isIntersecting).map((e) => e.target as HTMLVideoElement);
            entries.filter((e) => e.isIntersecting).forEach((entry) => {
                const video = entry.target as HTMLVideoElement;
                // When the marquee loops, every visible clip hands its pixels to
                // its twin in the other copy. The twin paused wherever it last
                // left the screen, so it takes the outgoing clip's position or the
                // footage visibly rewinds. A twin that is paused off screen has no
                // position worth copying.
                const key = videoKeys.current.get(video);
                const twin = key ? videos.current.get(twinKey(key)) : undefined;
                if (twin && !twin.paused) {
                    video.currentTime = twin.currentTime;
                }
                // Autoplay is a request, not a guarantee: a browser can still
                // refuse it, and the poster stays up when it does.
                void video.play().catch(() => undefined);
            });
            // Pausing runs last so the twins above still read their live position.
            leaving.forEach((video) => video.pause());
        }, { rootMargin: CLIP_PRELOAD_MARGIN });
        clipObserver.current = observer;
        // Ref callbacks run before effects, so the first cards are already
        // registered by the time this observer exists.
        videos.current.forEach((video) => observer.observe(video));
        return () => {
            observer.disconnect();
            clipObserver.current = null;
        };
    }, []);

    const registerVideo = useCallback((key: string, el: HTMLVideoElement | null) => {
        const previous = videos.current.get(key);
        if (previous && previous !== el) {
            clipObserver.current?.unobserve(previous);
            videos.current.delete(key);
            videoKeys.current.delete(previous);
        }
        if (el) {
            videos.current.set(key, el);
            videoKeys.current.set(el, key);
            clipObserver.current?.observe(el);
        }
    }, []);

    const { items } = data;
    if (items.length === 0) { return null; }

    const labels = { gmvLabel: data.gmvLabel, viewsLabel: data.viewsLabel };

    return (
        <section
            ref={ref}
            className={`fb-athletes${revealed ? ' faves-reveal-group--in' : ''}`}
        >
            <div className="fb-athletes__header">
                <span className="fb-athletes__eyebrow faves-eyebrow--uppercase faves-reveal">{data.eyebrow}</span>
                <h2 className="fb-athletes__heading faves-display faves-reveal">{data.heading}</h2>
            </div>
            <div className="fb-athletes__viewport">
                {/* The track carries the list twice while the marquee animates. It
                    travels exactly -50%, so the second copy is under the cursor at
                    the moment the animation restarts and the loop has no visible
                    seam. The copy is decorative and hidden from assistive tech. */}
                <ul
                    className="fb-athletes__track"
                    style={{
                        '--fb-athletes-marquee-duration': `${(items.length * MARQUEE_SECONDS_PER_CARD).toFixed(1)}s`,
                    } as React.CSSProperties}
                >
                    {items.map((item, i) => (
                        <AthleteCard
                            key={`a-${i}`}
                            item={item}
                            labels={labels}
                            revealDelayMs={i * REVEAL_STEP_MS}
                            registerVideo={registerVideo}
                            videoKey={`a-${i}`}
                        />
                    ))}
                    {marqueeAnimates && items.map((item, i) => (
                        <AthleteCard
                            key={`b-${i}`}
                            item={item}
                            labels={labels}
                            revealDelayMs={i * REVEAL_STEP_MS}
                            registerVideo={registerVideo}
                            videoKey={`b-${i}`}
                            clone
                        />
                    ))}
                </ul>
            </div>
        </section>
    );
}
